import { createContext } from "react";
import type { MyPayEnvironment } from "../../generated/core-api";
import type { SplitCostingImportManagerReturn } from "../hooks/useSplitCostingImportInvocation";
import type { SplitCostingRow, SplitCostingParseError } from "../utils";

export interface SplitCostingFailedUploadAttempt {
  fileName: string;
  file: File | null;
  rawCsvText: string | null;
  parseErrors: SplitCostingParseError[];
}

/**
 * Context value for the split costing import wizard.
 *
 * Company info (code, env, name) is passed in as props from
 * `SplitCostingImportLayout` — not stored here. The provider mounts only once
 * a company is validated, so all three fields are always populated when the
 * context is in scope.
 */
export interface SplitCostingWizardContextValue {
  /** Validated company code. */
  companyCode: string;
  /** Company name returned by the API at validation time. */
  companyName: string;
  /** Datapay environment validated at the company context gate. */
  mypayEnvironment: MyPayEnvironment;

  // ── Upload step state ─────────────────────────────────────────────────
  /** Original file name — needed for the R-GOV-3 audit record. */
  fileName: string | null;
  /** Selected file retained for the compact replacement dropzone. */
  file: File | null;
  /** Original CSV text retained for future issue export without reparsing. */
  rawCsvText: string | null;
  /**
   * Successfully parsed rows from the upload step.
   * Null until the user uploads a file that parses without errors.
   */
  parsedRows: SplitCostingRow[] | null;
  /**
   * Parse errors from the most recent upload attempt.
   * Non-empty when the file could not be fully parsed.
   * Legacy wizard state only; the single-page experience uses
   * `failedUploadAttempt` so a rejected replacement cannot overwrite a usable
   * parsed batch.
   */
  parseErrors: SplitCostingParseError[];
  /** Latest rejected single-page upload, kept separate from the usable batch. */
  failedUploadAttempt: SplitCostingFailedUploadAttempt | null;
  /** True while the layout owns a context-change or navigation confirmation. */
  isParentModalOpen: boolean;

  /** Store the outcome of a successful parse. */
  setUploadSuccess: (
    fileName: string,
    rows: SplitCostingRow[],
    file?: File | null,
    rawCsvText?: string | null
  ) => void;
  /** Store parse errors from a failed upload attempt (clears any prior successful rows). */
  setUploadErrors: (
    fileName: string,
    errors: SplitCostingParseError[],
    file?: File | null,
    rawCsvText?: string | null
  ) => void;
  /** Store rejected single-page diagnostics without changing the usable batch. */
  setFailedUploadAttempt: (
    fileName: string,
    errors: SplitCostingParseError[],
    file?: File | null,
    rawCsvText?: string | null
  ) => void;
  /** Reset the upload state. */
  clearUpload: () => void;

  // ── Validate/commit invocation state ──────────────────────────────────
  /**
   * Single `useSplitCostingImportInvocation` instance, instantiated once at
   * `SplitCostingWizardProvider` (not per-screen). This lets `ValidateScreen`
   * kick off `commit()` and immediately navigate to `ResultScreen` (step 6,
   * C4) while the commit poll keeps running against the *same* instance —
   * a screen-local instance would be torn down by the route change and lose
   * the in-flight commit. Mirrors `pay-component-import`'s `WizardProvider`,
   * which lifts `useWorkflowCommit` the same way for the same reason.
   */
  manager: SplitCostingImportManagerReturn;
}

export const SplitCostingWizardCtx = createContext<
  SplitCostingWizardContextValue | undefined
>(undefined);
